/**
 * Safely execute an async operation with mount checking.
 * Returns null if the component is unmounted during the operation.
 *
 * This prevents state updates on unmounted components and avoids
 * Firestore ca9 assertion failures.
 *
 * @param operation - The async operation to execute
 * @param isMounted - Function that returns true if component is mounted
 * @param options - Optional configuration
 * @returns The result of the operation, or null if component unmounted
 *
 * @example
 * const result = await safeAsync(
 *   () => getDocs(query),
 *   isMounted,
 *   { logContext: 'fetchUsers' }
 * )
 *
 * if (!result) return // Component unmounted
 * // Safe to use result
 */
export async function safeAsync<T>(
  operation: () => Promise<T>,
  isMounted: () => boolean,
  options?: { logContext?: string }
): Promise<T | null> {
  try {
    const result = await operation()

    if (!isMounted()) {
      const context = options?.logContext || 'safeAsync'
      console.warn(`[${context}] Component unmounted, discarding operation result`)
      return null
    }

    return result
  } catch (error) {
    if (!isMounted()) {
      const context = options?.logContext || 'safeAsync'
      console.warn(`[${context}] Component unmounted during error, discarding error`)
      return null
    }
    throw error
  }
}
